import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { bugReportApi } from '../../api/bugReportApi'
import { ApiError } from '../../api/httpClient'
import { uploadFiles } from '../../utils/files'
import { BugIcon } from './icons'
import { Spinner } from './Spinner'

const MAX_IMAGES = 3

/** Header button that opens a small form for reporting a bug; persisted via POST /api/bug-reports. */
export function BugReportButton() {
  const { t } = useTranslation()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [message, setMessage] = useState('')
  const [images, setImages] = useState<string[]>([])
  const [uploadingImages, setUploadingImages] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function open() {
    setMessage('')
    setImages([])
    setSubmitted(false)
    setError(null)
    dialogRef.current?.showModal()
  }

  function close() {
    dialogRef.current?.close()
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    try {
      setUploadingImages(true)
      const urls = await uploadFiles(e.target.files)
      setImages((prev) => {
        const combined = [...prev, ...urls]
        if (combined.length > MAX_IMAGES) {
          setError(t('bugReport.tooManyImages', { max: MAX_IMAGES }))
        }
        return combined.slice(0, MAX_IMAGES)
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : t('bugReport.errorFallback'))
    } finally {
      setUploadingImages(false)
      e.target.value = ''
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      await bugReportApi.create({ message, page: window.location.pathname, imageUrls: images })
      setSubmitted(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('bugReport.errorFallback'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <button type="button" className="bug-report-button" onClick={open} aria-label={t('bugReport.button')}>
        <BugIcon className="bug-report-icon" />
        <span className="btn-label">{t('bugReport.button')}</span>
      </button>
      <dialog ref={dialogRef} className="bug-report-dialog" aria-label={t('bugReport.title')}>
        {submitted ? (
          <div className="stack">
            <p>{t('bugReport.success')}</p>
            <button type="button" onClick={close}>
              {t('bugReport.close')}
            </button>
          </div>
        ) : (
          <form className="stack" onSubmit={handleSubmit}>
            <p className="dialog-title">{t('bugReport.title')}</p>
            <p className="muted">{t('bugReport.description')}</p>
            <label>
              {t('bugReport.textareaLabel')}
              <textarea
                rows={5}
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('bugReport.placeholder')}
              />
            </label>
            <label>
              {t('bugReport.imagesLabel', { max: MAX_IMAGES })}
              <input
                type="file"
                accept="image/*"
                multiple
                disabled={images.length >= MAX_IMAGES || uploadingImages}
                onChange={handleImageUpload}
              />
            </label>
            {uploadingImages && <Spinner label={t('common.uploading')} />}
            {images.length > 0 && (
              <div className="thumb-row">
                {images.map((url, i) => (
                  <div className="thumb" key={i}>
                    <img src={url} alt={t('bugReport.imageAlt', { index: i + 1 })} />
                    <button
                      type="button"
                      className="thumb-remove"
                      aria-label={t('bugReport.removeImage')}
                      onClick={() => setImages((prev) => prev.filter((_, idx) => idx !== i))}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            {error && <p className="bug-report-error">{error}</p>}
            <div className="actions">
              <button type="button" className="btn-outline" onClick={close} disabled={submitting}>
                {t('bugReport.cancel')}
              </button>
              <button type="submit" disabled={submitting || message.trim() === ''}>
                {t('bugReport.submit')}
              </button>
            </div>
          </form>
        )}
      </dialog>
    </>
  )
}
